import { useState, useEffect, useCallback, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import {
  fetchActivite,
  fetchEtatsSortie,
  fetchRessources,
  fetchRapportsDocuments,
  type Period,
  type ActiviteData,
  type EtatsSortieData,
  type RessourcesData,
  type RapportsDocumentsData,
} from "@/api/etablissementStatistiques";
import { DonutChart } from "./stats/DonutChart";
import { LineChart } from "./stats/LineChart";
import { HorizontalBar } from "./stats/HorizontalBar";
import { GaugeChart } from "./stats/GaugeChart";

// ─── Constantes labels & couleurs ─────────────────────────────────────────────

const PERIOD_OPTIONS: Array<{ value: Period; label: string }> = [
  { value: "30j", label: "30 derniers jours" },
  { value: "60j", label: "60 derniers jours" },
  { value: "90j", label: "90 derniers jours" },
  { value: "6m", label: "6 derniers mois" },
  { value: "9m", label: "9 derniers mois" },
  { value: "12m", label: "12 derniers mois" },
  { value: "1a", label: "1 an" },
];

const TYPE_CONSULT_LABELS: Record<string, string> = {
  consultation_generale: "Générale",
  consultation_specialiste: "Spécialiste",
  consultation_urgence: "Urgence",
  consultation_telemedecine: "Télémédecine",
  consultation_chirurgie: "Chirurgie",
  consultation_maternite: "Maternité",
  consultation_pediatrie: "Pédiatrie",
  consultation_psychiatrie: "Psychiatrie",
};

const STATUT_CONSULT_LABELS: Record<string, string> = {
  ouverte: "Ouverte",
  fermee: "Fermée",
  annulee: "Annulée",
};

const ETAT_LABELS: Record<string, string> = {
  gueri: "Guéri",
  en_traitement: "En traitement",
  sorti_sans_avis_medical: "Sorti sans avis",
  decede: "Décédé",
  sans_etat: "Sans état",
};

const ETAT_COLORS: Record<string, string> = {
  gueri: "#22c55e",
  en_traitement: "#00553E",
  sorti_sans_avis_medical: "#f97316",
  decede: "#ef4444",
  sans_etat: "#94a3b8",
};

const LIT_STATUT_LABELS: Record<string, string> = {
  disponible: "Disponible",
  occupe: "Occupé",
  maintenance: "Maintenance",
  hors_service: "Hors service",
};

const LIT_STATUT_COLORS = ["#22c55e", "#f97316", "#3b82f6", "#94a3b8"];

const RAPPORT_TYPE_LABELS: Record<string, string> = {
  sante_globale: "Santé globale",
  gestion_stock: "Gestion stock",
  ressources_medicales: "Ressources méd.",
  performance: "Performance",
  financier: "Financier",
  qualite: "Qualité",
  autre: "Autre",
};

const RAPPORT_STATUT_LABELS: Record<string, string> = {
  termine: "Terminé",
  en_cours: "En cours",
  erreur: "Erreur",
  annule: "Annulé",
};

const RAPPORT_STATUT_COLORS = ["#22c55e", "#3b82f6", "#ef4444", "#94a3b8"];

// ─── Sous-composants ──────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  color = "default",
  subtitle,
}: {
  label: string;
  value: string | number;
  color?: "default" | "green" | "orange" | "red" | "blue";
  subtitle?: string;
}) {
  const valueClass =
    color === "green"
      ? "text-green-700"
      : color === "orange"
        ? "text-orange-500"
        : color === "red"
          ? "text-red-600"
          : color === "blue"
            ? "text-blue-600"
            : "text-[#0B1F16]";

  return (
    <div className="rounded-[10px] border bg-white p-4" style={{ borderColor: "#E5E7EB" }}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${valueClass}`}>{value}</p>
      {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
    </div>
  );
}

function AlertCard({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-4 text-base font-semibold text-[#0B1F16]">{children}</h2>
  );
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rounded-[12px] border bg-white p-5"
      style={{ borderColor: "#E5E7EB" }}
    >
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#00553E] border-t-transparent" />
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function Statistiques() {
  const [period, setPeriod] = useState<Period>("30j");

  const [activite, setActivite] = useState<ActiviteData | null>(null);
  const [activiteLoading, setActiviteLoading] = useState(true);

  const [etats, setEtats] = useState<EtatsSortieData | null>(null);
  const [etatsLoading, setEtatsLoading] = useState(true);

  const [ressources, setRessources] = useState<RessourcesData | null>(null);
  const [ressourcesLoading, setRessourcesLoading] = useState(true);

  const [rapports, setRapports] = useState<RapportsDocumentsData | null>(null);
  const [rapportsLoading, setRapportsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const loadRessourcesEtRapports = useCallback(() => {
    fetchRessources().then((res) => {
      if (res.ok) setRessources(res.data);
      setRessourcesLoading(false);
    });

    setRapportsLoading(true);
    fetchRapportsDocuments().then((res) => {
      if (res.ok) setRapports(res.data);
      setRapportsLoading(false);
    });
  }, []);

  // Recharge activité + états pour la période courante
  const loadPeriodData = useCallback((p: Period) => {
    setActiviteLoading(true);
    setEtatsLoading(true);

    fetchActivite(p).then((res) => {
      if (res.ok) setActivite(res.data);
      setActiviteLoading(false);
    });

    fetchEtatsSortie(p).then((res) => {
      if (res.ok) setEtats(res.data);
      setEtatsLoading(false);
    });
  }, []);

  // Tout recharger : ressources/rapports + période courante — utilisé au montage, sur clic
  // "Actualiser", et par le polling périodique ci-dessous.
  const loadAll = useCallback(() => {
    loadRessourcesEtRapports();
    loadPeriodData(period);
    setLastRefresh(new Date());
  }, [loadRessourcesEtRapports, loadPeriodData, period]);

  useEffect(() => {
    loadPeriodData(period);
  }, [period, loadPeriodData]);

  // Charge ressources + rapports une seule fois au montage, puis rafraîchit tout périodiquement
  // — les statistiques sont calculées à la volée côté backend (pas de cache), mais rien ne
  // déclenchait de refetch une fois la page ouverte. `periodRef` évite une closure figée sur la
  // période initiale dans l'intervalle.
  const periodRef = useRef(period);
  periodRef.current = period;

  useEffect(() => {
    loadRessourcesEtRapports();
    setLastRefresh(new Date());
    const interval = setInterval(() => {
      loadRessourcesEtRapports();
      loadPeriodData(periodRef.current);
      setLastRefresh(new Date());
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Alertes globales
  const alertes: string[] = [];
  if (ressources) {
    if ((ressources.produits?.alertes?.rupturesStock ?? 0) > 0)
      alertes.push(`${ressources.produits.alertes!.rupturesStock} produit(s) en rupture de stock`);
    if ((ressources.produits?.alertes?.expires ?? 0) > 0)
      alertes.push(`${ressources.produits.alertes!.expires} produit(s) expirés`);

    const totalSangAlerte = Object.values(ressources.banqueSang?.parGroupeRhesus ?? {})
      .reduce((sum, g) => sum + (g.expiree ?? 0) + (g.rejetee ?? 0), 0);
    if (totalSangAlerte > 0)
      alertes.push(`${totalSangAlerte} poche(s) de sang expirées ou rejetées`);
  }
  if (rapports && (rapports.notifications?.nonLues ?? 0) > 0)
    alertes.push(`${rapports.notifications!.nonLues} notification(s) non lue(s)`);

  return (
    <div className="space-y-6">
      {/* Titre + sélecteur période */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-[#0B1F16]">Statistiques</h1>
        <div className="flex flex-wrap items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-[#0B1F16]/50">
              Mis à jour à {lastRefresh.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button
            type="button"
            onClick={loadAll}
            className="rounded-[8px] border px-3 py-2 text-sm font-medium text-[#00553E] hover:bg-[#00553E]/5"
            style={{ borderColor: "#D1E2DD" }}
          >
            Actualiser
          </button>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
            className="rounded-[8px] border px-3 py-2 text-sm text-[#0B1F16] focus:outline-none focus:ring-2 focus:ring-[#00553E]"
            style={{ borderColor: "#D1E2DD" }}
          >
            {PERIOD_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Alertes */}
      {alertes.length > 0 && (
        <div className="space-y-2">
          {alertes.map((msg, i) => (
            <AlertCard key={i} message={msg} />
          ))}
        </div>
      )}

      {/* Activité + États de sortie */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* ── Activité ── */}
        <SectionCard>
          <SectionTitle>Activité de l'établissement</SectionTitle>
          {activiteLoading ? (
            <Spinner />
          ) : activite ? (
            <div className="space-y-5">
              {/* KPI */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <StatCard
                  label="Consultations totales"
                  value={(activite.consultations?.total ?? 0).toLocaleString()}
                />
                <StatCard
                  label="Sur la période"
                  value={(activite.consultations?.surPeriode ?? 0).toLocaleString()}
                  color="blue"
                />
                <StatCard
                  label="Professionnels actifs"
                  value={activite.professionnelsActifs ?? 0}
                  color="green"
                />
                <StatCard
                  label="Patients distincts"
                  value={activite.patientsDistincts ?? 0}
                  color="green"
                />
              </div>

              {/* Évolution mensuelle */}
              {activite.consultations?.evolutionMensuelle?.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                    Évolution mensuelle
                  </p>
                  <LineChart
                    data={activite.consultations.evolutionMensuelle.map((m) => ({
                      label: m.mois,
                      value: m.total,
                    }))}
                    height={220}
                  />
                </div>
              )}

              {/* Types de consultations */}
              {activite.consultations?.parType && (
                <div>
                  <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                    Types de consultations
                  </p>
                  <HorizontalBar
                    data={Object.entries(activite.consultations.parType)
                      .sort((a, b) => b[1] - a[1])
                      .map(([k, v]) => ({
                        name: TYPE_CONSULT_LABELS[k] ?? k,
                        value: v,
                      }))}
                  />
                </div>
              )}

              {/* Statuts */}
              {activite.consultations?.parStatut && (
                <div>
                  <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                    Statuts des consultations
                  </p>
                  <DonutChart
                    data={Object.entries(activite.consultations.parStatut).map(
                      ([k, v]) => ({
                        name: STATUT_CONSULT_LABELS[k] ?? k,
                        value: v,
                      })
                    )}
                    colors={["#3b82f6", "#22c55e", "#94a3b8"]}
                    height={240}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-gray-500">
              Données indisponibles
            </p>
          )}
        </SectionCard>

        {/* ── États de sortie ── */}
        <SectionCard>
          <SectionTitle>États de sortie</SectionTitle>
          {etatsLoading ? (
            <Spinner />
          ) : etats ? (
            <div className="space-y-5">
              {/* Alerte si consultations non clôturées */}
              {etats.consultationsNonCloturees > 0 && (
                <AlertCard
                  message={`${etats.consultationsNonCloturees} consultation(s) ouverte(s) sans état de sortie`}
                />
              )}

              {/* KPI taux */}
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  label="Guérison"
                  value={`${etats.taux?.guerison ?? 0}%`}
                  color="green"
                />
                <StatCard
                  label="Sans avis médical"
                  value={`${etats.taux?.sortiSansAvis ?? 0}%`}
                  color="orange"
                />
              </div>

              {/* KPI total */}
              <StatCard
                label="Consultations clôturées sur la période"
                value={(etats.totalFermees ?? 0).toLocaleString()}
              />

              {/* Donut issues cliniques */}
              {etats.parEtat && (
                <div>
                  <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                    Distribution des issues cliniques
                  </p>
                  <DonutChart
                    data={Object.entries(etats.parEtat).map(([k, v]) => ({
                      name: ETAT_LABELS[k] ?? k,
                      value: v,
                    }))}
                    colors={Object.keys(etats.parEtat).map(
                      (k) => ETAT_COLORS[k] ?? "#94a3b8"
                    )}
                    height={280}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-gray-500">
              Données indisponibles
            </p>
          )}
        </SectionCard>
      </div>

      {/* Ressources */}
      <SectionCard>
        <SectionTitle>Ressources & Capacités</SectionTitle>
        {ressourcesLoading ? (
          <Spinner />
        ) : ressources ? (
          <div className="space-y-6">
            {ressources.lits && (
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Jauge occupation lits */}
                <div>
                  <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                    Taux d'occupation des lits
                  </p>
                  <GaugeChart
                    value={ressources.lits.tauxOccupation ?? 0}
                    label="Occupation"
                    alertThreshold={80}
                    height={200}
                  />
                  <p className="mt-1 text-center text-xs text-gray-400">
                    {ressources.lits.parStatut?.occupe ?? 0} occupés /{" "}
                    {ressources.lits.total ?? 0} lits totaux
                  </p>
                </div>

                {/* Donut répartition lits */}
                {ressources.lits.parStatut && (
                  <div>
                    <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                      Répartition des lits
                    </p>
                    <DonutChart
                      data={Object.entries(ressources.lits.parStatut).map(
                        ([k, v]) => ({
                          name: LIT_STATUT_LABELS[k] ?? k,
                          value: v,
                        })
                      )}
                      colors={LIT_STATUT_COLORS}
                      height={240}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Table banque de sang */}
            {ressources.banqueSang?.parGroupeRhesus && (
              <div>
                <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                  Banque de sang — stock par groupe
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-gray-500" style={{ borderColor: "#E5E7EB" }}>
                        <th className="pb-2 pr-4 font-medium">Groupe / Rhésus</th>
                        <th className="pb-2 pr-3 font-medium text-green-700">Dispo.</th>
                        <th className="pb-2 pr-3 font-medium text-blue-600">Réservée</th>
                        <th className="pb-2 pr-3 font-medium text-gray-500">Utilisée</th>
                        <th className="pb-2 pr-3 font-medium text-orange-500">Expirée</th>
                        <th className="pb-2 font-medium text-red-600">Rejetée</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(ressources.banqueSang.parGroupeRhesus).map(
                        ([groupe, stats]) => (
                          <tr
                            key={groupe}
                            className="border-b"
                            style={{ borderColor: "#F3F4F6" }}
                          >
                            <td className="py-2 pr-4 font-semibold text-[#0B1F16]">
                              {groupe}
                            </td>
                            <td className="py-2 pr-3 text-green-700">
                              {stats.disponible}
                            </td>
                            <td className="py-2 pr-3 text-blue-600">
                              {stats.reservee}
                            </td>
                            <td className="py-2 pr-3 text-gray-500">
                              {stats.utilisee}
                            </td>
                            <td
                              className={`py-2 pr-3 ${(stats.expiree ?? 0) > 0 ? "font-semibold text-orange-500" : "text-gray-400"}`}
                            >
                              {stats.expiree ?? 0}
                            </td>
                            <td
                              className={`py-2 ${(stats.rejetee ?? 0) > 0 ? "font-semibold text-red-600" : "text-gray-400"}`}
                            >
                              {stats.rejetee ?? 0}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-500">
            Données indisponibles
          </p>
        )}
      </SectionCard>

      {/* Rapports & Documents */}
      <SectionCard>
        <SectionTitle>Rapports & Documents</SectionTitle>
        {rapportsLoading ? (
          <Spinner />
        ) : rapports ? (
          <div className="space-y-5">
            {/* Notifications non lues */}
            {(rapports.notifications?.nonLues ?? 0) > 0 && (
              <div
                className="flex items-center justify-between rounded-[8px] border px-4 py-2"
                style={{ borderColor: "#D1E2DD", background: "#DCEFE9" }}
              >
                <span className="text-sm font-medium text-[#0B1F16]">
                  Notifications non lues
                </span>
                <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">
                  {rapports.notifications!.nonLues}
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {/* Rapports par type */}
              {rapports.rapports?.parType && (
                <div>
                  <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                    Rapports par type
                  </p>
                  <DonutChart
                    data={Object.entries(rapports.rapports.parType).map(
                      ([k, v]) => ({
                        name: RAPPORT_TYPE_LABELS[k] ?? k,
                        value: v,
                      })
                    )}
                    height={260}
                  />
                </div>
              )}

              {/* Statut des rapports */}
              {rapports.rapports?.parStatut && (
                <div>
                  <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                    Statut des rapports
                  </p>
                  <DonutChart
                    data={Object.entries(rapports.rapports.parStatut).map(
                      ([k, v]) => ({
                        name: RAPPORT_STATUT_LABELS[k] ?? k,
                        value: v,
                      })
                    )}
                    colors={RAPPORT_STATUT_COLORS}
                    height={260}
                  />
                </div>
              )}
            </div>

            {/* Table rapports récents */}
            {(rapports.rapports?.recents?.length ?? 0) > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium text-[#0B1F16]">
                  Rapports récents
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr
                        className="border-b text-left text-xs text-gray-500"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        <th className="pb-2 pr-4 font-medium">Titre</th>
                        <th className="pb-2 pr-4 font-medium">Type</th>
                        <th className="pb-2 pr-4 font-medium">Statut</th>
                        <th className="pb-2 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rapports.rapports.recents.map((r) => (
                        <tr
                          key={r._id}
                          className="border-b"
                          style={{ borderColor: "#F3F4F6" }}
                        >
                          <td className="py-2 pr-4 text-[#0B1F16]">
                            {r.titre}
                          </td>
                          <td className="py-2 pr-4 text-gray-500">
                            {RAPPORT_TYPE_LABELS[r.type] ?? r.type}
                          </td>
                          <td className="py-2 pr-4">
                            <span
                              className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                                r.statut === "termine"
                                  ? "bg-green-100 text-green-700"
                                  : r.statut === "en_cours"
                                    ? "bg-blue-100 text-blue-700"
                                    : r.statut === "erreur"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {RAPPORT_STATUT_LABELS[r.statut] ?? r.statut}
                            </span>
                          </td>
                          <td className="py-2 text-xs text-gray-400">
                            {new Date(r.dateCreation).toLocaleDateString(
                              "fr-FR"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-500">
            Données indisponibles
          </p>
        )}
      </SectionCard>
    </div>
  );
}
